import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { auth } from '../firebase.js';
import { IoLogOutOutline, IoDiamond, IoWarningOutline } from 'react-icons/io5';
import { GiBroccoli } from "react-icons/gi";
import { FaRegHeart } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import BackButton from '../components/ui/atoms/BackButton';
import BottomSheet from '../components/Shared/BottomSheet';
import { Button } from '../components/ui/atoms/Button/Button';
import SettingsMenuItem from '../components/ui/molecules/SettingsMenuItem';
import { MdKeyboardArrowRight } from 'react-icons/md';

const SettingsMenu: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { backendUser, deleteAccount, refreshBackendUser } = useAuth() as any;
  const { showToast } = useToast() as any;
  const adminEmail = 'admin@panpals.de';
  const [deleting, setDeleting] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    const status = params.get('checkout');
    if (!status) return;
    const alreadyShown = sessionStorage.getItem('toastShown');

    if (!alreadyShown) {
      if (status === 'success') {
        showToast({
          type: 'success',
          message: 'Your monthly PanPals Subscription is now activated.',
        });
        refreshBackendUser();
      } else if (status === 'cancel') {
        showToast({
          type: 'info',
          message: 'Checkout was canceled. Please try again later.',
        });
      }
      sessionStorage.setItem('toastShown', 'true');
    }

    return () => {
      sessionStorage.removeItem('toastShown');
    };
  }, [params, showToast, refreshBackendUser]);

  const confirmLogout = async () => {
    await auth.signOut();
    navigate('/auth');
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      navigate('/auth');
    } catch (err) {
      console.error(err);
      showToast({ message: 'Failed to delete account.', type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="h-full bg-white">
      <header className="flex items-center justify-between p-4 pt-10 relative">
        <BackButton onClick={() => navigate('/app/profile')} />
        <Button
          onClick={() => setShowLogout(true)}
          variant="header"
          className="text-m px-3 py-1 flex items-center gap-2 ml-auto fixed top-0 right-4 z-50"
        >
          <IoLogOutOutline className="w-5 h-5" />
          Log Out
        </Button>
      </header>

      <main className="flex-grow px-4 pb-4 space-y-2 overflow-auto">
        <SettingsMenuItem
          label="Profile Settings"
          onClick={() => navigate('/app/settings/account')}
          icon={<MdKeyboardArrowRight />}
        />

        <SettingsMenuItem
          label="Dietary Preferences"
          onClick={() => {
            if (location.pathname.startsWith('/app/onboarding')) {
              navigate('/app/onboarding/settings/preferences');
            } else if (backendUser?.isSubscribed) {
              navigate('/app/settings/preferences');
            } else {
              navigate('/app/subscribe');
            }
          }}
          highlight={!backendUser?.isSubscribed}
          icon={
            !backendUser?.isSubscribed ? (
              <span className="text-purple-600 text-sm font-medium flex items-center gap-1">
                <IoDiamond className="text-purple-500" /> Premium
              </span>
            ) : (
              <GiBroccoli />
            )
          }
        />

        <SettingsMenuItem label="Terms of Service" onClick={() => navigate('/terms')} icon={<MdKeyboardArrowRight />} />
        <SettingsMenuItem label="Privacy Policy" onClick={() => navigate('/privacy')} icon={<MdKeyboardArrowRight />} />
        <SettingsMenuItem label="Imprint" onClick={() => navigate('/imprint')} icon={<MdKeyboardArrowRight />} />

        <SettingsMenuItem
          label="Give us feedback"
          onClick={() => navigate('/app/settings/feedback')}
          icon={<FaRegHeart />}
        />
      </main>

      {backendUser?.email !== adminEmail && (
        <footer className="p-4">
          <SettingsMenuItem
            label="Delete My Account"
            onClick={() => setShowDelete(true)}
            danger
            icon={<IoWarningOutline />}
          />
        </footer>
      )}

      <BottomSheet isOpen={showLogout} onClose={() => setShowLogout(false)}>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-4">Log Out?</h2>
          <p className="text-sm text-gray-600 mb-6">
            Are you sure you want to log out of your account?
          </p>
          <button
            onClick={confirmLogout}
            className="w-full bg-red-600 text-white rounded-full py-3 mb-2"
          >
            Log Out
          </button>
          <button
            onClick={() => setShowLogout(false)}
            className="w-full bg-gray-200 rounded-full py-3"
          >
            Cancel
          </button>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={showDelete} onClose={() => setShowDelete(false)}>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-4 text-red-600 font-black">
            Delete Your Account?
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            This action <strong>cannot</strong> be reversed. All your data will be permanently deleted.
          </p>
          <div className="flex gap-5 mb-6">
            <Button variant="transparent" onClick={() => setShowDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="black"
              className="bg-red-600 hover:bg-red-400"
              onClick={confirmDelete}
            >
              Delete My Account
            </Button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
};

export default SettingsMenu;
